import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { AuditChartDatum } from './auditDetailTypes';
import { accordionSummarySx, accordionSx } from './auditDetailShared';

interface AuditStatsSectionProps {
  isDark: boolean;
  expanded: boolean;
  totalFindings: number;
  kritData: AuditChartDatum[];
  reviewData: AuditChartDatum[];
  onToggle: (_: React.SyntheticEvent, isExpanded: boolean) => void;
}

const AuditStatsSection: React.FC<AuditStatsSectionProps> = ({
  isDark,
  expanded,
  totalFindings,
  kritData,
  reviewData,
  onToggle,
}) => {
  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.[0]) return null;

    const datum = payload[0];
    return (
      <Box
        sx={{
          bgcolor: isDark ? '#1e2444' : '#fff',
          border: `1px solid ${isDark ? '#2e3560' : '#dfe3e8'}`,
          borderRadius: 2,
          px: 1.5,
          py: 0.75,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: datum.payload.color, flexShrink: 0 }}
        />
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: isDark ? '#e8edf3' : '#1a2332', fontSize: '0.8rem' }}
        >
          {datum.name}: {datum.value}
        </Typography>
      </Box>
    );
  };

  const renderLegend = (data: AuditChartDatum[]) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center', mt: 1 }}>
      {data.map((entry) => (
        <Box key={entry.name} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: entry.color, flexShrink: 0 }} />
          <Typography
            variant="caption"
            sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500, lineHeight: 1 }}
          >
            {entry.name}: {entry.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const renderChart = (title: string, data: AuditChartDatum[]) => (
    <Box sx={{ flex: '1 1 280px', maxWidth: 360, textAlign: 'center' }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          textTransform: 'uppercase',
          fontSize: '0.65rem',
          letterSpacing: '0.08em',
          color: 'text.disabled',
          display: 'block',
          mb: 1,
        }}
      >
        {title}
      </Typography>
      <ResponsiveContainer width="100%" height={160}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            cx="50%"
            cy="50%"
            outerRadius={65}
            innerRadius={32}
            paddingAngle={2}
            strokeWidth={0}
          >
            {data.map((entry, index) => (
              <Cell key={`${entry.name}-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <RechartsTooltip content={<CustomTooltip />} />
        </PieChart>
      </ResponsiveContainer>
      {renderLegend(data)}
    </Box>
  );

  return (
    <Accordion expanded={expanded} onChange={onToggle} sx={accordionSx(isDark)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx(isDark)}>
        <BarChartOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Stats
        </Typography>
        <Chip
          label={`${totalFindings} Schwachstellen`}
          size="small"
          sx={{
            ml: 1,
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 22,
            bgcolor: isDark ? 'rgba(66,165,245,0.15)' : 'rgba(66,165,245,0.1)',
            color: '#42a5f5',
            border: '1px solid rgba(66,165,245,0.3)',
          }}
        />
      </AccordionSummary>
      <AccordionDetails sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
          {renderChart('Kritikalität', kritData)}
          {renderChart('Schwachstellen-Status', reviewData)}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default AuditStatsSection;
